'use client'

import Image from 'next/image'
import { useState } from 'react'
import { Sparkles, ArrowRight } from 'lucide-react'

type HeroBlockProps = {
  blockType: 'hero'
  id?: string | null
  badge?: string | null
  heading?: string | null
  headingHighlight?: string | null
  subtitle?: string | null
  heroImage?: { url?: string | null; alt?: string | null; focalX?: number | null; focalY?: number | null } | number | null
}

export const HeroBlockComponent: React.FC<HeroBlockProps> = ({
  badge,
  heading,
  headingHighlight,
  subtitle,
  heroImage,
}) => {
  const [imageActive, setImageActive] = useState(false)
  const media = typeof heroImage === 'object' ? heroImage : null
  const heroImageUrl = media?.url ?? null
  const heroImageAlt = media?.alt ?? 'Hero afbeelding'
  const focalX = media?.focalX ?? 50
  const focalY = media?.focalY ?? 50

  // Split heading to highlight the specified word
  const renderHeading = () => {
    if (!heading) return null
    if (!headingHighlight) return heading

    const parts = heading.split(headingHighlight)
    if (parts.length === 1) return heading

    return (
      <>
        {parts[0]}
        <span className="bg-gradient-to-r from-[#085e41] to-[#2D5A3D] bg-clip-text text-transparent">
          {headingHighlight}
        </span>
        {parts[1]}
      </>
    )
  }

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#085e41]/15 via-[#085e41]/5 to-background">
      <div className="container py-16 md:py-20 lg:py-24">
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-16">
          {/* Left side - Content */}
          <div className="max-w-xl space-y-8">
            {badge && (
              <div className="inline-flex items-center gap-2 rounded-full border border-[#085e41]/20 bg-white/80 px-4 py-2 text-sm font-medium text-[#085e41] shadow-sm backdrop-blur-sm">
                <Sparkles className="h-4 w-4" />
                <span>{badge}</span>
              </div>
            )}

            <div className="space-y-4">
              <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-foreground md:text-5xl lg:text-6xl">
                {renderHeading()}
              </h1>

              {subtitle && (
                <p className="max-w-lg text-lg leading-relaxed text-muted-foreground md:text-xl">
                  {subtitle}
                </p>
              )}
            </div>

            <a
              href="#cursussen"
              className="group inline-flex items-center gap-2 rounded-full bg-[#085e41] px-7 py-3.5 font-semibold text-white shadow-lg shadow-[#085e41]/25 transition-all hover:gap-3 hover:bg-[#2D5A3D] hover:shadow-xl hover:shadow-[#085e41]/30"
            >
              Bekijk cursussen
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>

          {/* Right side - Image with circular styling */}
          {heroImageUrl && (
            <div
              className="group relative mx-auto mt-8 aspect-square w-full max-w-xs sm:max-w-sm lg:mt-0 lg:max-w-md"
              data-active={imageActive ? '' : undefined}
              onClick={() => setImageActive((v) => !v)}
            >
              {/* Decorative circles */}
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#085e41]/10 transition-all duration-500 group-hover:-right-10 group-hover:-top-10 group-hover:bg-[#085e41]/15 group-data-[active]:-right-10 group-data-[active]:-top-10 group-data-[active]:bg-[#085e41]/15" />
              <div className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full bg-[#085e41]/15 transition-all duration-500 group-hover:-bottom-6 group-hover:-left-6 group-hover:bg-[#085e41]/20 group-data-[active]:-bottom-6 group-data-[active]:-left-6 group-data-[active]:bg-[#085e41]/20" />
              <div className="absolute -right-4 bottom-12 h-16 w-16 rounded-full bg-[#085e41]/20 transition-all duration-500 group-hover:-right-6 group-hover:bottom-10 group-hover:bg-[#085e41]/25 group-data-[active]:-right-6 group-data-[active]:bottom-10 group-data-[active]:bg-[#085e41]/25" />
              <div className="absolute left-8 top-0 h-12 w-12 rounded-full border-2 border-[#085e41]/20 transition-all duration-500 group-hover:left-6 group-hover:-top-2 group-hover:border-[#085e41]/30 group-data-[active]:left-6 group-data-[active]:-top-2 group-data-[active]:border-[#085e41]/30" />
              <div className="absolute -bottom-8 right-16 h-20 w-20 rounded-full border-2 border-[#085e41]/15 transition-all duration-500 group-hover:-bottom-10 group-hover:right-14 group-hover:border-[#085e41]/25 group-data-[active]:-bottom-10 group-data-[active]:right-14 group-data-[active]:border-[#085e41]/25" />

              {/* Glow behind image */}
              <div className="absolute inset-4 rounded-full bg-[#085e41]/10 blur-2xl transition-all duration-500 group-hover:bg-[#085e41]/15 group-data-[active]:bg-[#085e41]/15" />

              {/* Main circular image */}
              <div className="relative h-full w-full overflow-hidden rounded-full shadow-2xl ring-6 ring-[#085e41] ring-offset-background">
                <Image
                  src={heroImageUrl}
                  alt={heroImageAlt}
                  fill
                  sizes="(min-width: 1024px) 448px, (min-width: 640px) 384px, 320px"
                  className="object-cover"
                  style={{ objectPosition: `${focalX}% ${focalY}%` }}
                  priority
                  fetchPriority="high"
                />
              </div>

              {/* Logo badge - bottom right */}
              <div className="absolute -bottom-4 -right-4 flex h-32 w-32 items-center justify-center rounded-full border-[6px] border-[#085e41] bg-[#085e41] p-4 shadow-xl sm:h-40 sm:w-40 lg:-bottom-6 lg:-right-6 lg:h-44 lg:w-44">
                <Image
                  src="/licht_tekst.png"
                  alt="Voeding, sport & leefstijl logo"
                  width={150}
                  height={150}
                  sizes="(min-width: 1024px) 176px, (min-width: 640px) 160px, 128px"
                  className="object-contain"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
